import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/features/auth/providers/AuthProvider";

// Layouts
import PublicLayout from "@/layouts/PublicLayout";
import StudentLayout from "@/layouts/StudentLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Public pages
import HomePage from "@/pages/HomePage";
import CourseCatalogPage from "@/pages/CourseCatalogPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import QuienesSomosPage from "@/pages/QuienesSomosPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import BundleDetailPage from "@/pages/BundleDetailPage";
import AcceptInvitationPage from "@/pages/public/AcceptInvitationPage";
import CertificateVerifyPage from "@/pages/public/CertificateVerifyPage";
import EnterprisePage from "@/pages/public/EnterprisePage";
import OfferingsPage from "@/pages/public/OfferingsPage";
import OfferingDetailPage from "@/pages/public/OfferingDetailPage";
import ContactPage from "@/pages/public/ContactPage";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import MaterialsCatalogPage from "@/pages/MaterialsCatalogPage";
import MaterialDetailPage from "@/pages/MaterialDetailPage";

// Protected pages
import DashboardPage from "@/pages/student/DashboardPage";
import MyCoursesPage from "@/pages/student/MyCoursesPage";
import CoursePlayerPage from "@/pages/student/CoursePlayerPage";
import CheckoutPage from "@/pages/student/CheckoutPage";
import BundleCheckoutPage from "@/pages/student/BundleCheckoutPage";
import CheckoutSuccessPage from "@/pages/student/CheckoutSuccessPage";
import WishlistPage from "@/pages/student/WishlistPage";
import FinalExamPage from "@/pages/student/FinalExamPage";

// Admin pages
import AdminDashboardPage from "@/pages/admin/DashboardPage";
import CoursesManagementPage from "@/pages/admin/CoursesPage";
import CourseEditorPage from "@/pages/admin/CourseEditorPage";
import ChapterEditorPage from "@/pages/admin/ChapterEditorPage";
import ChapterEditorPageV2 from "@/pages/admin/ChapterEditorPageV2";
import CoursePreviewPage from "@/pages/admin/CoursePreviewPage";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import BundlesPage from "@/pages/admin/BundlesPage";
import BundleEditorPage from "@/pages/admin/BundleEditorPage";
import StudentsPage from "@/pages/admin/StudentsPage";
import StudentDetailPage from "@/pages/admin/StudentDetailPage";
import PaymentsPage from "@/pages/admin/PaymentsPage";
import FinancePage from "@/pages/admin/FinancePage";
import CourseCreationWizardPage from "@/pages/admin/CourseCreationWizardPage";
import AITasksPage from "@/pages/admin/AITasksPage";
import TeamMembersPage from "@/pages/admin/TeamMembersPage";
import EnterpriseContactsPage from "@/pages/admin/EnterpriseContactsPage";
import FinalExamEditorPage from "@/pages/admin/FinalExamEditorPage";
import AdminMaterialsPage from "@/pages/admin/MaterialsPage";
import AdminOfferingsPage from "@/pages/admin/OfferingsPage";
import AdminOfferingEditorPage from "@/pages/admin/OfferingEditorPage";
import AdminMaterialEditorPage from "@/pages/admin/MaterialEditorPage";
import MyMaterialsPage from "@/pages/student/MyMaterialsPage";
import MaterialCheckoutPage from "@/pages/student/MaterialCheckoutPage";

// Route guards
import ProtectedRoute from "@/router/ProtectedRoute";
import AdminRoute from "@/router/AdminRoute";

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/quienes-somos" element={<QuienesSomosPage />} />
          <Route path="/courses" element={<CourseCatalogPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/bundles/:slug" element={<BundleDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/empresas" element={<EnterprisePage />} />
          <Route path="/programas" element={<OfferingsPage />} />
          <Route path="/programas/:slug" element={<OfferingDetailPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/terminos" element={<TermsPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
          <Route path="/materiales" element={<MaterialsCatalogPage />} />
          <Route path="/materiales/:slug" element={<MaterialDetailPage />} />
        </Route>

        {/* Standalone public pages (no layout) */}
        <Route path="/invitation/:token" element={<AcceptInvitationPage />} />
        <Route path="/verify" element={<CertificateVerifyPage />} />

        {/* Protected student routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<StudentLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/my-courses" element={<MyCoursesPage />} />
            <Route path="/mis-materiales" element={<MyMaterialsPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/checkout/:courseId" element={<CheckoutPage />} />
            <Route
              path="/checkout/bundle/:bundleId"
              element={<BundleCheckoutPage />}
            />
            <Route
              path="/checkout/material/:slug"
              element={<MaterialCheckoutPage />}
            />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          </Route>
          <Route
            path="/learn/:courseSlug/:chapterId?"
            element={<CoursePlayerPage />}
          />
          <Route
            path="/courses/:courseId/final-exam"
            element={<FinalExamPage />}
          />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/courses" element={<CoursesManagementPage />} />
            <Route
              path="/admin/courses/new"
              element={<CourseCreationWizardPage />}
            />
            <Route
              path="/admin/courses/new-manual"
              element={<CourseEditorPage />}
            />
            <Route path="/admin/courses/:id" element={<CourseEditorPage />} />
            <Route
              path="/admin/courses/:courseId/chapters/new"
              element={<ChapterEditorPage />}
            />
            <Route
              path="/admin/courses/:courseId/chapters/:id/edit"
              element={<ChapterEditorPageV2 />}
            />
            <Route
              path="/admin/courses/:courseId/chapters/:id/edit-classic"
              element={<ChapterEditorPage />}
            />
            <Route
              path="/admin/courses/:courseId/final-exam"
              element={<FinalExamEditorPage />}
            />
            <Route path="/admin/categories" element={<CategoriesPage />} />
            <Route path="/admin/materials" element={<AdminMaterialsPage />} />
            <Route path="/admin/programas" element={<AdminOfferingsPage />} />
            <Route
              path="/admin/programas/new"
              element={<AdminOfferingEditorPage />}
            />
            <Route
              path="/admin/programas/:id/edit"
              element={<AdminOfferingEditorPage />}
            />
            <Route
              path="/admin/materials/new"
              element={<AdminMaterialEditorPage />}
            />
            <Route
              path="/admin/materials/:id/edit"
              element={<AdminMaterialEditorPage />}
            />
            <Route path="/admin/bundles" element={<BundlesPage />} />
            <Route path="/admin/bundles/new" element={<BundleEditorPage />} />
            <Route
              path="/admin/bundles/:id/edit"
              element={<BundleEditorPage />}
            />
            <Route path="/admin/students" element={<StudentsPage />} />
            <Route path="/admin/students/:id" element={<StudentDetailPage />} />
            <Route path="/admin/payments" element={<PaymentsPage />} />
            <Route path="/admin/finance" element={<FinancePage />} />
            <Route path="/admin/ai-tasks" element={<AITasksPage />} />
            <Route path="/admin/team" element={<TeamMembersPage />} />
            <Route
              path="/admin/enterprise-contacts"
              element={<EnterpriseContactsPage />}
            />
          </Route>
          {/* Full-screen preview without layout */}
          <Route
            path="/admin/courses/:courseId/preview"
            element={<CoursePreviewPage />}
          />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
